import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem
} from '@mui/material';
import { X, Search } from 'lucide-react';

const HubsDrawer = ({
  drawerOpen,
  setDrawerOpen,
  allAvailableHubs = [],
  addRelHub
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroups, setSelectedGroups] = useState({});

  const filteredHubs = useMemo(() => {
    if (!Array.isArray(allAvailableHubs)) return [];
    if (!searchQuery.trim()) return allAvailableHubs;
    const q = searchQuery.toLowerCase().trim();
    return allAvailableHubs.filter(
      (h) => (h.t && h.t.toLowerCase().includes(q)) || (h.u && h.u.toLowerCase().includes(q))
    );
  }, [allAvailableHubs, searchQuery]);

  const handleGroupChange = (hubUrl, group) => {
    setSelectedGroups((prev) => ({ ...prev, [hubUrl]: group }));
  };

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          maxWidth: '100vw',
          backgroundColor: '#FFFFFF',
          borderLeft: '1px solid #E2E8F0',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}
      >
        <Box sx={{ pr: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#1E293B', mb: 0.5 }}>
            Add a Related Hub
          </Typography>
          <Typography sx={{ color: '#64748B', fontSize: 11.5, lineHeight: 1.45 }}>
            Select an existing hub page to link on this hub. Choose the section block it belongs to.
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setDrawerOpen(false)}
          sx={{
            color: '#64748B',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            p: 0.75,
            '&:hover': { color: '#1E293B', backgroundColor: '#F8FAFC' }
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      {/* Search Input */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #EEF2F6', backgroundColor: '#F8FAFC' }}>
        <TextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter available hubs by title or url..."
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: '#94A3B8' }}>
                <Search size={15} />
              </Box>
            ),
            endAdornment: searchQuery ? (
              <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: '#94A3B8', p: 0.5 }}>
                <X size={13} />
              </IconButton>
            ) : null
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              fontSize: 12.5,
              height: 36,
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#CBD5E1' },
              '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
            }
          }}
        />
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1, backgroundColor: '#FFFFFF' }}>
        {filteredHubs.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 3,
              border: '1px dashed #E2E8F0',
              borderRadius: '8px',
              backgroundColor: '#F8FAFC'
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E293B', mb: 0.5 }}>
              No hubs found
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: 11.5 }}>
              {searchQuery ? `No hubs match "${searchQuery}".` : 'No other hubs available.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {filteredHubs.map((h) => {
              const currentGroup = selectedGroups[h.u] || 'explore';

              return (
                <Paper
                  key={h.u}
                  elevation={0}
                  sx={{
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'none'
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      title={h.t}
                      sx={{
                        fontWeight: 600,
                        fontSize: 12.5,
                        color: '#1E293B',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {h.t}
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: 11, mt: 0.25 }}>
                      {h.u} · {h.n} {h.n === 1 ? 'retreat' : 'retreats'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Select
                      size="small"
                      value={currentGroup}
                      onChange={(e) => handleGroupChange(h.u, e.target.value)}
                      sx={{
                        fontSize: 11.5,
                        height: 30,
                        backgroundColor: '#F8FAFC',
                        borderRadius: '5px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' }
                      }}
                    >
                      <MenuItem value="similar">Similar</MenuItem>
                      <MenuItem value="nearby">Nearby</MenuItem>
                      <MenuItem value="explore">Explore</MenuItem>
                    </Select>

                    <Button
                      variant="contained"
                      size="small"
                      disableRipple
                      onClick={() => addRelHub(h.u, h.t, h.n, currentGroup)}
                      sx={{
                        backgroundColor: '#1E293B',
                        color: '#FFFFFF',
                        fontSize: 11,
                        fontWeight: 500,
                        textTransform: 'none',
                        px: 1.25,
                        height: 30,
                        borderRadius: '5px',
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                      }}
                    >
                      + Add
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', textAlign: 'right' }}>
        <Button
          variant="outlined"
          size="small"
          disableRipple
          onClick={() => setDrawerOpen(false)}
          sx={{
            borderColor: '#CBD5E1',
            color: '#1E293B',
            fontWeight: 500,
            fontSize: 12,
            px: 2,
            height: 32,
            borderRadius: '6px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { borderColor: '#94A3B8', backgroundColor: '#FFFFFF' }
          }}
        >
          Close
        </Button>
      </Box>
    </Drawer>
  );
};

export default React.memo(HubsDrawer);
